import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";

import {
	Typography,
	Box,
	Divider,
	ListItem,
	List,
	ListItemIcon,
	ListItemText,
	IconButton,
	TextField,
	Menu,
	MenuItem,
	Button,
	Skeleton,
	Tooltip,
	Autocomplete,
	Chip,
	Checkbox,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SettingsIcon from "@mui/icons-material/Settings";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SaveIcon from "@mui/icons-material/Save";

import { useAppDispatch } from "../../store/hooks.ts";
import { calculateEndTime } from "../calendar/helper/eventRendering.tsx";
import { setSelectedSession } from "../../store/slices/sessionSlice.ts";
import { setSelectedEvent } from "../../store/slices/eventSlice.ts";
import {
	setCalendarDate,
	setCalendarView,
	setSelectedEntityType,
	patchSurgeonSpecialtyOnEvents,
} from "../../store/slices/calendarSlice.ts";

import globalAxios from "../../globalAxios";
import { SERVER_URL } from "../../config";
import { playSuccessSave } from "../../components/sounds/index.ts";

import { SPECIALTY_OPTIONS_BASE } from "../../constants/index.ts";

// ---------------- Helpers / validators ----------------
const VALIDATORS = {
	email: (v: string) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
	phoneish: (v: string) => v === "" || /^[\d\s()+-]{3,}$/.test(v), // digits, spaces, (), +, -
};

// ---------------- Component ----------------
const SurgeonView: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { id } = useParams();

	// Data
	const [surgeon, setSurgeon] = useState<any | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(true);

	// Editing
	const [isEditing, setIsEditing] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [saving, setSaving] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);

	// Local copy for edits
	const [localSurgeon, setLocalSurgeon] = useState<any | null>(null);
	const [originalSurgeon, setOriginalSurgeon] = useState<any | null>(null);

	const changesMade = useMemo(() => {
		if (!localSurgeon || !originalSurgeon) return false;
		return !_.isEqual(localSurgeon, originalSurgeon);
	}, [localSurgeon, originalSurgeon]);

	const specialtyOptions = useMemo(() => {
		const existing = Array.isArray(localSurgeon?.specialty)
			? (localSurgeon!.specialty as unknown as string[])
					.filter(Boolean)
					.map((s) => String(s).trim())
			: [];

		const merged = Array.from(
			new Set([...SPECIALTY_OPTIONS_BASE, ...existing])
		);

		// Sort A→Z for consistent UX
		return merged.sort((a, b) => a.localeCompare(b));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localSurgeon, SPECIALTY_OPTIONS_BASE]);

	// Fetch
	useEffect(() => {
		const fetchSurgeon = async () => {
			if (!id) return;
			setLoadingDetail(true);
			console.debug("[SURGEON_VIEW] fetch start", { id });

			try {
				// Prefer rich endpoint with sessions included
				const res = await globalAxios.get(
					`${SERVER_URL}/surgeon/${id}`
				);
				setSurgeon(res.data);
				setLocalSurgeon(res.data);
				setOriginalSurgeon(res.data);
				console.debug("[SURGEON_VIEW] loaded from /surgeon/:id");
			} catch (errRich) {
				console.warn(
					"[SURGEON_VIEW] rich endpoint failed, falling back",
					errRich
				);
				try {
					const res = await globalAxios.get(
						`${SERVER_URL}/surgeon/${id}`
					);
					setSurgeon(res.data);
					setLocalSurgeon(res.data);
					setOriginalSurgeon(res.data);
					console.debug("[SURGEON_VIEW] loaded from /surgeon/:id");
				} catch (errBasic) {
					console.error(
						"[SURGEON_VIEW] failed to load surgeon",
						errBasic
					);
				}
			} finally {
				setLoadingDetail(false);
			}
		};

		fetchSurgeon();
	}, [id]);

	// Handlers
	const handleBack = () => navigate(-1);

	const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget);
	};
	const handleMenuClose = () => setAnchorEl(null);

	const handleToggleEdit = () => {
		handleMenuClose();
		setTimeout(() => setIsEditing(true), 0);
	};

	const handleCancel = () => {
		handleMenuClose();
		setIsEditing(false);
		setLocalSurgeon(originalSurgeon);
	};

	const handleSave = async () => {
		if (!localSurgeon) return;
		setSaving(true);
		try {
			const payload = {
				first_name: localSurgeon.first_name ?? "",
				last_name: localSurgeon.last_name ?? "",
				rooms_address: localSurgeon.rooms_address ?? "",
				rooms_email: localSurgeon.rooms_email ?? "",
				rooms_phone: localSurgeon.rooms_phone ?? "",
				rooms_fax: localSurgeon.rooms_fax ?? "",
				mobile: localSurgeon.mobile ?? "",
				billing_type: localSurgeon.billing_type ?? "",
				rooms_software:
					localSurgeon.rooms_software === "" ||
					localSurgeon.rooms_software === undefined
						? null
						: Number(localSurgeon.rooms_software),
				specialty: Array.isArray(localSurgeon.specialty)
					? localSurgeon.specialty
					: [],
			};

			console.debug("[SURGEON_VIEW] save payload", payload);

			const res = await globalAxios.put(
				`${SERVER_URL}/surgeon/${localSurgeon.id}`,
				payload
			);

			if (res.status === 200 || res.status === 204) {
				console.debug("[SURGEON_VIEW] save ok");

				// Prefer canonical server response; fall back to local+payload
				const updated = (res.data &&
					(res.data.surgeon ?? res.data)) || {
					...localSurgeon,
					...payload,
				};

				// ✅ Update all views/state
				setSurgeon(updated);
				setLocalSurgeon(updated);
				setOriginalSurgeon(updated);

				// ✅ Patch calendar so the legend reflects the new specialties right away
				dispatch(
					patchSurgeonSpecialtyOnEvents({
						surgeonId: updated.id,
						specialty: updated.specialty ?? [],
					})
				);
				console.debug(
					"[SURGEON_VIEW] patched calendar events with new specialty"
				);

				playSuccessSave();
				setSaveSuccess(true);

				// Close menu first to avoid flicker, then exit edit mode
				setAnchorEl(null);
				setIsEditing(false);
			} else {
				console.error(
					"[SURGEON_VIEW] unexpected save response",
					res.status
				);
			}
		} catch (err) {
			console.error("[SURGEON_VIEW] save failed", err);
		} finally {
			setSaving(false);
			setTimeout(() => setSaveSuccess(false), 1500);
		}
	};

	const handleSessionClick = async (session: any) => {
		if (!session) return;
		const dateStr = session.date_of_service;
		console.debug("[SURGEON_VIEW] open session", { sessionId: session.id });
		try {
			const res = await globalAxios.get(
				`${SERVER_URL}/search/session/${session.id}`
			);
			const fullSession = res.data;

			const startTime = fullSession.start || "08:00";
			const sessionStart = `${fullSession.date_of_service}T${startTime}:00`;
			const sessionEnd = calculateEndTime(
				fullSession.date_of_service,
				startTime,
				null
			);

			dispatch(
				setSelectedSession({
					title: fullSession.hospital?.name || "Session",
					id: fullSession.id,
					start: sessionStart,
					end: sessionEnd,
					surgeon: {
						...fullSession.surgeon,
						name: `${fullSession.surgeon?.first_name || ""} ${
							fullSession.surgeon?.last_name || ""
						}`,
					},
					hospital: fullSession.hospital,
					events: fullSession.events,
				})
			);

			if (fullSession.events?.length) {
				dispatch(setSelectedEvent(fullSession.events[0]));
				dispatch(setSelectedEntityType("event"));
			} else {
				dispatch(setSelectedEntityType("session"));
			}

			dispatch(setCalendarDate(dateStr));
			dispatch(setCalendarView("dayGridMonth"));
			navigate("/calendar");
		} catch (err) {
			console.error("[SURGEON_VIEW] fetch session failed", err);
		}
	};

	return (
		<Box sx={{ maxWidth: "1200px", width: "100%", px: 2, mt: 2 }}>
			<Box
				sx={{
					display: "flex",
					mb: 4,
					flexWrap: "nowrap",
					alignItems: "stretch",
				}}
			>
				{/* Back */}
				<Button
					variant="outlined"
					onClick={handleBack}
					sx={{
						borderRadius: "8px 0 0 8px",
						borderRight: "none",
						backgroundColor: "grey.200",
						color: "text.primary",
						borderColor: "grey.400",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						minWidth: 56,
						px: 1,
						py: 1.5,
						"&:hover": {
							backgroundColor: "grey.300",
							borderColor: "grey.400",
						},
						borderTopRightRadius: 0,
						borderBottomRightRadius: 0,
					}}
				>
					<ArrowBackIcon />
				</Button>

				{/* Surgeon Details */}
				<Box
					sx={{
						border: "1px solid #ccc",
						borderLeft: "none",
						borderRadius: "0 8px 8px 0",
						flex: 1,
						backgroundColor: "#fafafa",
					}}
				>
					{/* Header */}
					<Box
						sx={{
							borderRadius: "0 7px 0 0",
							backgroundColor: (t) => t.palette.primary.main,
							color: (t) => t.palette.primary.contrastText,
							px: 2,
							pt: 0.2,
							pb: 0.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Typography variant="h6" fontWeight="bold">
							Surgeon Details
						</Typography>

						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							{saving ? (
								<SaveIcon
									color="warning"
									fontSize="small"
									sx={{
										animation: "pulseInward 1s infinite",
									}}
								/>
							) : saveSuccess ? (
								<SaveIcon
									color="success"
									fontSize="small"
									sx={{
										animation:
											"fadeOutSuccess 1.5s forwards",
									}}
								/>
							) : changesMade ? (
								<Tooltip title="Unsaved changes">
									<WarningAmberIcon
										color="warning"
										fontSize="small"
									/>
								</Tooltip>
							) : null}

							<IconButton
								size="small"
								sx={{ color: "inherit", p: 0 }}
								onClick={handleMenuClick}
							>
								<SettingsIcon />
							</IconButton>
						</Box>

						<Menu
							anchorEl={anchorEl}
							open={open}
							onClose={handleMenuClose}
							transformOrigin={{
								horizontal: "right",
								vertical: "top",
							}}
							anchorOrigin={{
								horizontal: "right",
								vertical: "bottom",
							}}
						>
							{!isEditing ? (
								<MenuItem onClick={handleToggleEdit}>
									Edit
								</MenuItem>
							) : (
								<>
									<MenuItem onClick={handleSave}>
										Save
									</MenuItem>
									<MenuItem onClick={handleCancel}>
										Cancel
									</MenuItem>
								</>
							)}
						</Menu>
					</Box>

					{/* Body */}
					<Box sx={{ px: 2, py: 1 }}>
						{loadingDetail ? (
							<>
								<Skeleton height={24} width="40%" />
								<Skeleton height={24} width="60%" />
								<Skeleton height={24} width="50%" />
							</>
						) : isEditing ? (
							<>
								<TextField
									label="First Name"
									size="small"
									value={localSurgeon?.first_name || ""}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) =>
										setLocalSurgeon({
											...localSurgeon,
											first_name: e.target.value,
										})
									}
									required
								/>
								<TextField
									label="Last Name"
									size="small"
									value={localSurgeon?.last_name || ""}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) =>
										setLocalSurgeon({
											...localSurgeon,
											last_name: e.target.value,
										})
									}
									required
								/>
								<Divider sx={{ my: 1 }} />
								<TextField
									label="Rooms Address"
									size="small"
									value={localSurgeon?.rooms_address || ""}
									fullWidth
									multiline
									minRows={2}
									sx={{ mb: 1 }}
									onChange={(e) =>
										setLocalSurgeon({
											...localSurgeon,
											rooms_address: e.target.value,
										})
									}
								/>
								<TextField
									label="Rooms Email"
									size="small"
									value={localSurgeon?.rooms_email || ""}
									fullWidth
									sx={{ mb: 1 }}
									error={
										!!localSurgeon?.rooms_email &&
										!VALIDATORS.email(
											localSurgeon.rooms_email
										)
									}
									helperText={
										!!localSurgeon?.rooms_email &&
										!VALIDATORS.email(
											localSurgeon.rooms_email
										)
											? "Invalid email address"
											: ""
									}
									onChange={(e) =>
										setLocalSurgeon({
											...localSurgeon,
											rooms_email: e.target.value,
										})
									}
								/>
								<TextField
									label="Rooms Phone"
									size="small"
									value={localSurgeon?.rooms_phone || ""}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) => {
										const v = e.target.value;
										if (VALIDATORS.phoneish(v)) {
											setLocalSurgeon({
												...localSurgeon,
												rooms_phone: v,
											});
										}
									}}
								/>
								<TextField
									label="Rooms Fax"
									size="small"
									value={localSurgeon?.rooms_fax || ""}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) => {
										const v = e.target.value;
										if (VALIDATORS.phoneish(v)) {
											setLocalSurgeon({
												...localSurgeon,
												rooms_fax: v,
											});
										}
									}}
								/>
								<TextField
									label="Mobile"
									size="small"
									value={localSurgeon?.mobile || ""}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) => {
										const v = e.target.value;
										if (VALIDATORS.phoneish(v)) {
											setLocalSurgeon({
												...localSurgeon,
												mobile: v,
											});
										}
									}}
								/>
								<TextField
									label="Billing Type"
									size="small"
									value={localSurgeon?.billing_type || ""}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) =>
										setLocalSurgeon({
											...localSurgeon,
											billing_type: e.target.value,
										})
									}
									placeholder="e.g. No-Gap, Known-Gap, Private"
								/>
								<TextField
									label="Rooms Software (ID)"
									size="small"
									type="number"
									value={
										localSurgeon?.rooms_software === null ||
										localSurgeon?.rooms_software ===
											undefined
											? ""
											: localSurgeon.rooms_software
									}
									fullWidth
									sx={{ mb: 1 }}
									onChange={(e) =>
										setLocalSurgeon({
											...localSurgeon,
											rooms_software:
												e.target.value === ""
													? null
													: Number(e.target.value),
										})
									}
								/>

								{/* Specialty tags */}
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={specialtyOptions}
									value={
										(localSurgeon?.specialty as
											| string[]
											| undefined) ?? []
									}
									onChange={(_, newValue) => {
										const cleaned = (
											newValue as string[]
										).filter((v) =>
											specialtyOptions.includes(v)
										);
										console.debug(
											"[SURGEON_VIEW] specialty changed ->",
											cleaned
										);
										setLocalSurgeon({
											...localSurgeon,
											specialty: cleaned,
										});
									}}
									renderOption={(
										props,
										option,
										{ selected }
									) => (
										<li {...props}>
											<Checkbox
												checked={selected}
												sx={{ mr: 1, p: 0.5 }}
											/>
											{option}
										</li>
									)}
									renderTags={(
										value: readonly string[],
										getTagProps
									) =>
										value.map(
											(option: string, index: number) => (
												<Chip
													variant="filled"
													size="small"
													label={option}
													{...getTagProps({ index })}
												/>
											)
										)
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Specialty"
											size="small"
											placeholder="Select specialties"
											sx={{ mb: 1 }}
										/>
									)}
								/>
							</>
						) : (
							<>
								<Typography variant="body2">
									<strong>Name:</strong>{" "}
									{(surgeon?.first_name || "—") +
										" " +
										(surgeon?.last_name || "")}
								</Typography>
								<Divider sx={{ my: 1 }} />
								<Typography variant="body2">
									<strong>Rooms Address:</strong>{" "}
									{surgeon?.rooms_address || "—"}
								</Typography>
								<Typography variant="body2">
									<strong>Rooms Email:</strong>{" "}
									{surgeon?.rooms_email || "—"}
								</Typography>
								<Typography variant="body2">
									<strong>Rooms Phone:</strong>{" "}
									{surgeon?.rooms_phone || "—"}
								</Typography>
								<Typography variant="body2">
									<strong>Rooms Fax:</strong>{" "}
									{surgeon?.rooms_fax || "—"}
								</Typography>
								<Typography variant="body2">
									<strong>Mobile:</strong>{" "}
									{surgeon?.mobile || "—"}
								</Typography>
								<Typography variant="body2">
									<strong>Billing Type:</strong>{" "}
									{surgeon?.billing_type || "—"}
								</Typography>
								<Typography variant="body2">
									<strong>Rooms Software (ID):</strong>{" "}
									{surgeon?.rooms_software ?? "—"}
								</Typography>

								<Typography
									variant="body2"
									sx={{ mt: 1, mb: 0.5 }}
								>
									<strong>Specialty:</strong>
								</Typography>
								<Box
									sx={{
										display: "flex",
										flexWrap: "wrap",
										gap: 0.5,
										mb: 1,
									}}
								>
									{Array.isArray(surgeon?.specialty) &&
									surgeon!.specialty.length > 0 ? (
										surgeon!.specialty.map(
											(s: string, i: number) => (
												<Chip
													key={`${s}-${i}`}
													size="small"
													label={s}
												/>
											)
										)
									) : (
										<Typography variant="body2">
											—
										</Typography>
									)}
								</Box>
							</>
						)}
					</Box>
				</Box>
			</Box>

			{/* Lower content: Linked Sessions (left) + Placeholder (right) */}
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
				{/* Linked Sessions */}
				<Box
					sx={{
						flex: 1,
						minWidth: { xs: "100%", md: "48%" },
						border: "1px solid #ccc",
						borderRadius: 2,
						backgroundColor: "#fafafa",
					}}
				>
					<Box
						sx={{
							borderRadius: "7px 7px 0 0",
							backgroundColor: (t) => t.palette.primary.main,
							color: (t) => t.palette.primary.contrastText,
							px: 2,
							pt: 0.2,
							pb: 0.5,
						}}
					>
						<Typography variant="h6" fontWeight="bold">
							Linked Sessions
						</Typography>
					</Box>

					<Box sx={{ px: 2 }}>
						{loadingDetail ? (
							Array.from({ length: 2 }).map((_, i) => (
								<Box
									key={i}
									sx={{
										display: "flex",
										alignItems: "center",
										mt: 1.5,
										mb: 1.5,
										gap: 2,
									}}
								>
									<Skeleton
										variant="circular"
										width={32}
										height={32}
									/>
									<Box sx={{ flex: 1 }}>
										<Skeleton height={20} width="80%" />
										<Skeleton height={16} width="60%" />
									</Box>
								</Box>
							))
						) : surgeon?.sessions?.length ? (
							<List dense>
								{surgeon.sessions.map((s: any) => (
									<ListItem
										key={s.id}
										button
										onClick={() => handleSessionClick(s)}
										sx={{
											cursor: "pointer",
											"&:hover": {
												backgroundColor: "#f5f5f5",
											},
										}}
									>
										<ListItemIcon>
											<MedicalServicesIcon />
										</ListItemIcon>
										<ListItemText
											primary={
												s.hospital?.name
													? `Hospital: ${s.hospital.name}`
													: `Session #${s.id}`
											}
											secondary={
												s.date_of_service
													? new Date(
															s.date_of_service
													  ).toLocaleDateString()
													: "No date"
											}
										/>
									</ListItem>
								))}
							</List>
						) : (
							<Typography variant="body2">
								No sessions found for this surgeon.
							</Typography>
						)}
					</Box>
				</Box>

				{/* Right placeholder */}
				<Box
					sx={{
						flex: 1,
						minWidth: { xs: "100%", md: "48%" },
						border: "1px dashed #ccc",
						borderRadius: 2,
						backgroundColor: "#f9f9f9",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						minHeight: "200px",
					}}
				>
					<Typography variant="body2" color="text.secondary">
						(New content coming soon…)
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default SurgeonView;
